/* Forged Racing — shipping estimator (Round 19, Task D).
   Wraps Shopify's native /cart/shipping_rates.json.

   HARD RULE baked into this file: nothing displayed here is invented.
   - Price comes from rate.price.
   - Transit time is rendered ONLY from rate.delivery_days, which this store
     really returns (carrier-calculated rates are enabled — USPS, UPS, DHL
     eCommerce all come back with real delivery_days/delivery_date). A rate
     with no usable delivery_days renders price only: there is deliberately
     no fallback range, no guess derived from the rate name, no filler.
   - Error copy is Shopify's own message lifted from the 422 body. When the
     body can't be parsed we say we couldn't get rates and do NOT claim to
     know why, because the response doesn't tell us.

   Written as setup(root) per instance: this snippet renders twice on /cart
   (the always-present drawer plus the page footer), so module-level state
   would have wired only the first one. */
(function () {
  'use strict';

  function money(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return null;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  /* Real carrier transit estimate, or null. Never a guess. */
  function transitText(rate) {
    var d = rate && rate.delivery_days;
    if (!d || !d.length) return null;
    var min = parseInt(d[0], 10);
    var max = parseInt(d.length > 1 ? d[1] : d[0], 10);
    if (isNaN(min) && isNaN(max)) return null;
    if (isNaN(min)) min = max;
    if (isNaN(max)) max = min;
    if (min === max) {
      return min + (min === 1 ? ' business day in transit' : ' business days in transit');
    }
    return min + '–' + max + ' business days in transit';
  }

  /* Shopify 422 bodies are field-keyed arrays of human-readable strings:
     {"zip":["Enter a valid ZIP code for California, United States"]} */
  function parseErrors(bodyText) {
    try {
      var obj = JSON.parse(bodyText);
      var msgs = [];
      Object.keys(obj).forEach(function (k) {
        var v = obj[k];
        if (Array.isArray(v)) { msgs = msgs.concat(v); }
        else if (typeof v === 'string') { msgs.push(v); }
      });
      return msgs.length ? msgs.join(' ') : null;
    } catch (e) {
      return null;
    }
  }

  function setup(root) {
    var panel = root.querySelector('[data-frg-ship-est-panel]');
    var toggle = root.querySelector('[data-frg-ship-est-toggle]');
    var form = root.querySelector('[data-frg-ship-est-form]');
    var emptyEl = root.querySelector('[data-frg-ship-est-empty]');
    var countrySel = root.querySelector('[data-frg-ship-est-country]');
    var provinceWrap = root.querySelector('[data-frg-ship-est-province-wrap]');
    var provinceSel = root.querySelector('[data-frg-ship-est-province]');
    var zipEl = root.querySelector('[data-frg-ship-est-zip]');
    var submitEl = root.querySelector('[data-frg-ship-est-submit]');
    var msgEl = root.querySelector('[data-frg-ship-est-msg]');
    var ratesEl = root.querySelector('[data-frg-ship-est-rates]');
    var busy = false;

    function showMsg(text, kind) {
      if (!msgEl) return;
      msgEl.textContent = text || '';
      msgEl.hidden = !text;
      msgEl.className = 'frg-ship-est__msg' + (kind ? ' is-' + kind : '');
    }

    function clearRates() {
      if (!ratesEl) return;
      ratesEl.innerHTML = '';
      ratesEl.hidden = true;
    }

    function renderRates(rates, currencyFallback) {
      clearRates();
      if (!rates || !rates.length) {
        /* 200 with an empty list: address is valid, nothing serves it.
           State that, don't invent a reason. */
        showMsg('No shipping rates are available for that address. Get in touch and we’ll see what we can do.', 'warn');
        return;
      }
      rates.forEach(function (rate) {
        var li = document.createElement('li');
        li.className = 'frg-ship-est__rate';

        var name = document.createElement('span');
        name.className = 'frg-ship-est__rate-name';
        name.textContent = rate.presentment_name || rate.name || 'Shipping';

        var price = document.createElement('span');
        price.className = 'frg-ship-est__rate-price';
        /* A genuine 0.00 rate is free shipping the store actually
           configured — label it, never hardcode a threshold. */
        price.textContent = (parseFloat(rate.price) === 0)
          ? 'Free'
          : (money(rate.price, rate.currency || currencyFallback) || '');

        var top = document.createElement('div');
        top.className = 'frg-ship-est__rate-top';
        top.appendChild(name);
        top.appendChild(price);
        li.appendChild(top);

        var transit = transitText(rate);
        if (transit) {
          var sub = document.createElement('span');
          sub.className = 'frg-ship-est__rate-sub';
          sub.textContent = transit;
          li.appendChild(sub);
        }
        ratesEl.appendChild(li);
      });
      ratesEl.hidden = false;
      showMsg('', null);
    }

    function estimate() {
      if (busy) return;
      var country = countrySel ? countrySel.value : '';
      var province = (provinceWrap && !provinceWrap.hidden && provinceSel) ? provinceSel.value : '';
      var zip = zipEl ? zipEl.value.trim() : '';
      if (!country) { showMsg('Choose a country first.', 'error'); return; }

      busy = true;
      if (submitEl) { submitEl.disabled = true; }
      clearRates();
      showMsg('Checking rates…', 'pending');

      var qs = 'shipping_address[zip]=' + encodeURIComponent(zip) +
               '&shipping_address[country]=' + encodeURIComponent(country) +
               '&shipping_address[province]=' + encodeURIComponent(province);

      fetch('/cart/shipping_rates.json?' + qs, { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          return r.text().then(function (t) { return { ok: r.ok, text: t }; });
        })
        .then(function (res) {
          if (res.ok) {
            var data = {};
            try { data = JSON.parse(res.text); } catch (e) { data = {}; }
            renderRates(data.shipping_rates, data.currency);
            return;
          }
          showMsg(
            parseErrors(res.text) ||
            'We couldn’t get shipping rates for that address. Double-check the details and try again.',
            'error'
          );
        })
        .catch(function () {
          showMsg('Couldn’t reach the shipping service. Check your connection and try again.', 'error');
        })
        .then(function () {
          busy = false;
          if (submitEl) { submitEl.disabled = false; }
        });
    }

    /* Province list rides on Shopify's own country_option_tags markup. */
    function syncProvinces() {
      if (!countrySel || !provinceWrap || !provinceSel) return;
      var opt = countrySel.options[countrySel.selectedIndex];
      var provinces = [];
      if (opt) {
        try { provinces = JSON.parse(opt.getAttribute('data-provinces') || '[]'); }
        catch (e) { provinces = []; }
      }
      provinceSel.innerHTML = '';
      if (!provinces.length) { provinceWrap.hidden = true; return; }
      provinces.forEach(function (p) {
        var o = document.createElement('option');
        o.value = p[0];
        o.textContent = p[1];
        provinceSel.appendChild(o);
      });
      provinceWrap.hidden = false;
    }

    /* Empty cart must never show a rate — including a $0 one. */
    function syncCartState() {
      if (!form || !emptyEl) return;
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var empty = !cart || cart.item_count === 0;
          form.hidden = empty;
          emptyEl.hidden = !empty;
          if (empty) { clearRates(); showMsg('', null); }
        })
        .catch(function () { /* keep whatever the server rendered */ });
    }

    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        var willOpen = panel.hidden;
        panel.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) { syncCartState(); }
      });
    }
    if (countrySel) { countrySel.addEventListener('change', syncProvinces); }
    if (submitEl) { submitEl.addEventListener('click', estimate); }
    if (zipEl) {
      zipEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); estimate(); }
      });
    }

    syncProvinces();

    /* The drawer re-renders its line items on every cart mutation; re-check
       so the estimator can't sit on a stale cart. */
    var items = document.querySelector('[data-forged-cart-items]');
    if (items && window.MutationObserver) {
      new MutationObserver(function () {
        if (panel && !panel.hidden) { clearRates(); showMsg('', null); syncCartState(); }
      }).observe(items, { childList: true });
    }
  }

  function init() {
    document.querySelectorAll('[data-frg-ship-est]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
